import { useEffect } from "react";
import { api } from "../../lib/api";
import { getAuthToken, notifyAuthLost, refreshAuthToken } from "../../lib/auth";
import { useLatest } from "../../hooks/useLatest";

// Module copy of ChatLayout's pure key helper (same pattern as useFavorites / useAndroidShare).
const fq = (vId: string, cId: string) => (cId.includes(':') ? cId : `${vId}:${cId}`);

type SocketLike = {
  connected: boolean;
  emit: (event: string, payload?: unknown) => unknown;
  on: (event: string, handler: (...args: any[]) => void) => unknown;
  off: (event: string, handler: (...args: any[]) => void) => unknown;
};

type Options<Member, OutboxItem> = {
  socket: SocketLike;
  /** Access token; sent in the socket handshake. A new token reconnects the socket. */
  token: string;
  /** Called with a refreshed token after an authentication failure. */
  onTokenChange: (token: string) => void;
  connect: () => void;
  disconnect: () => void;
  // What the user is looking at. Read at call time, so they are refs.
  currentVoidIdRef: { current: string };
  currentChannelIdRef: { current: string };
  channelsRef: { current: { id: string; type?: string | null }[] };
  /** Channels read locally whose read marker the server may not have caught up with yet. */
  pendingReadRef: { current: Set<string> };
  /** Holds the presence ping interval so it can be restarted on reconnect and stopped on cleanup. */
  heartbeatRef: { current: number | null };
  openChannelFromFqidRef: { current: (fqid: string) => void };
  setUnread: (updater: () => Record<string, number>) => void;
  setOutbox: (updater: (prev: OutboxItem[]) => OutboxItem[]) => void;
  setDmPageOpen: (open: boolean) => void;
  setMembers: (members: Member[]) => void;
  loadKanbanIfNeeded: (fqid: string) => void;
  loadFormIfNeeded: (fqid: string) => void;
};

/**
 * Connects the live socket and does the work that has to happen on every real (re)connect: refresh the
 * unread counts from the server, restart the presence ping, send messages queued while offline, apply a
 * pending notification deep link, re-join the current space and channel rooms, and reload that channel's
 * data and the member list. Also refreshes an expired token and reconnects.
 *
 * Moved out of ChatLayout unchanged. It re-runs only when the token changes (not on every channel switch);
 * the per-channel message handlers are registered separately.
 */
export function useSocketLifecycle<Member = unknown, OutboxItem = unknown>({
  socket,
  token,
  onTokenChange,
  connect,
  disconnect,
  currentVoidIdRef,
  currentChannelIdRef,
  channelsRef,
  pendingReadRef,
  heartbeatRef,
  openChannelFromFqidRef,
  setUnread,
  setOutbox,
  setDmPageOpen,
  setMembers,
  loadKanbanIfNeeded,
  loadFormIfNeeded,
}: Options<Member, OutboxItem>) {
  // onConnect runs on real (re)connects only; read the newest loaders through refs instead of the render it was created in.
  const loadKanbanIfNeededRef = useLatest(loadKanbanIfNeeded);
  const loadFormIfNeededRef = useLatest(loadFormIfNeeded);

  useEffect(() => {
    // ensure socket handshake includes the latest token
    (socket as any).auth = { ...(socket as any).auth, token };

    const onConnect = () => {
      socket.emit("void:list");
      // Hydrate unread counts from the server (the source of truth) so the badge
      // is consistent across devices instead of reflecting only what this device
      // happened to track locally. Runs on first connect and every reconnect.
      // localStorage still provides an instant count before this resolves.
      (async () => {
        try {
          const tok = getAuthToken() || '';
          const res = await api.getAuth('/unread', tok);
          const counts = (res && res.unread && typeof res.unread === 'object') ? (res.unread as Record<string, number>) : null;
          if (!counts) return;
          const activeFqid = fq(currentVoidIdRef.current, currentChannelIdRef.current);
          setUnread(() => {
            const next: Record<string, number> = {};
            for (const [cid, n] of Object.entries(counts)) {
              // The channel we're looking at gets marked read on switch/scroll —
              // don't flash a stale count for it.
              if (cid === activeFqid) continue;
              // A channel we've already read this session: the server advances the
              // watermark asynchronously over the socket and this HTTP snapshot can
              // race ahead of it, so honour our local read instead of resurfacing a
              // stale count. markUnreadTracked drops the entry when a new message
              // genuinely arrives, so real unread still shows.
              if (pendingReadRef.current.has(cid)) continue;
              const v = Number(n) || 0;
              if (v > 0) next[cid] = v;
            }
            return next;
          });
          // Once the server agrees a read-pending channel has no unread, stop
          // shadowing it so future counts for it flow through normally.
          for (const cid of Array.from(pendingReadRef.current)) {
            if (!(Number(counts[cid]) > 0)) pendingReadRef.current.delete(cid);
          }
        } catch { /* noop */ }
      })();
      // Kick off heartbeat to keep presence fresh
      try { if (heartbeatRef.current) clearInterval(heartbeatRef.current); } catch { /* noop */ }
      heartbeatRef.current = window.setInterval(() => {
        try { socket.emit('presence:ping', { ts: Date.now() }); } catch { /* noop */ }
      }, 15000);
      // Flush any queued outbox messages
      try {
        setOutbox(prev => {
          const items = [...prev];
          for (const it of items) { try { socket.emit('message:send', it); } catch { /* noop */ } }
          return prev; // Keep until ack; onNew will remove by tempId
        });
      } catch { /* noop */ }
      // Apply a pending DM deep link from a notification tap (set in main.tsx).
      // Must run before the regular void/channel switch so it takes priority.
      try {
        const raw = localStorage.getItem('pendingDmDeepLink');
        if (raw) {
          localStorage.removeItem('pendingDmDeepLink');
          const { spaceId, channelId } = JSON.parse(raw) as { spaceId: string; channelId: string };
          if (spaceId?.startsWith('dm_') && channelId) {
            openChannelFromFqidRef.current(`${spaceId}:${channelId}`);
            setDmPageOpen(false);
            return;
          }
        }
      } catch { /* noop */ }
      // Re-join the current space/channel rooms on (re)connect. Read live values
      // from refs — this effect no longer re-runs per navigation, so the closure
      // can't rely on currentVoidId/currentChannelId state. Navigation itself emits
      // these via switchVoid/switchChannel, so this only matters on connect/reconnect.
      const cvid = currentVoidIdRef.current;
      const ccid = currentChannelIdRef.current;
      if (cvid) {
        socket.emit("void:switch", { voidId: cvid });
        socket.emit("channel:list", { voidId: cvid });
        const fid = fq(cvid, ccid);
        socket.emit("channel:switch", { voidId: cvid, channelId: fid });
        const meta = channelsRef.current.find(c => c.id === ccid);
        if (meta && meta.type === 'kanban') { loadKanbanIfNeededRef.current(fid); }
        if (meta && meta.type === 'form') { loadFormIfNeededRef.current(fid); }
        // load members for current space
        (async () => {
          try {
            const tok = getAuthToken() || '';
            const res = await api.getAuth(`/spaces/members?spaceId=${encodeURIComponent(cvid)}`, tok);
            setMembers(res.members || []);
          } catch { /* noop */ }
        })();
      }
      // seed toneUrl so notifications use custom sound even before profile is reopened
      (async () => {
        try {
          const tok = getAuthToken() || '';
          const u = await api.getAuth('/users/me', tok);
          if (u?.toneUrl) localStorage.setItem('toneUrl', u.toneUrl);
          if (u?.nameColor) localStorage.setItem('nameColor', u.nameColor); else localStorage.removeItem('nameColor');
        } catch { /* noop */ }
      })();
    };
    const onDisconnect = () => {
      // LiveKit runs independently — a socket blip should not end the voice call.
    };
    const onError = async (err: any) => {
      console.error("socket error", err);
      const message = String(err?.message || err?.data?.message || err || '').toLowerCase();
      const authFailed =
        message.includes('invalid token') ||
        message.includes('missing token') ||
        message.includes('unauthorized') ||
        message.includes('authentication') ||
        message.includes('jwt');
      if (!authFailed) return;
      try {
        const nt = await refreshAuthToken();
        if (nt) {
          (socket as any).auth = { ...(socket as any).auth, token: nt };
          const tokenChanged = nt !== token;
          onTokenChange(nt);
          // When the token actually changes, onTokenChange updates `token` (an effect
          // dep), which re-runs this socket-lifecycle effect: cleanup disconnects and
          // the body reconnects, firing onConnect once. Only when the refreshed token
          // is unchanged (the effect won't re-run) do we reconnect explicitly. Doing
          // both used to fire two reconnects / two onConnects on the invalid-token path.
          if (!tokenChanged) {
            setTimeout(() => { try { connect(); } catch { /* noop */ } }, 50);
          }
          return;
        }
        notifyAuthLost();
      } catch {
        notifyAuthLost();
      }
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onError);

    connect();

    // Reconnect on returning to a tab whose socket dropped while hidden.
    const onVis = () => {
      if (document.visibilityState === "visible" && !socket.connected) connect();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onError);
      document.removeEventListener("visibilitychange", onVis);
      disconnect();
      try { if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null; } } catch { /* noop */ }
    };
    // Stable deps: connect once per real connection (and reconnect on a token
    // change), NOT on every channel/space switch. The presence heartbeat, void:list,
    // /unread hydration and outbox flush all live in onConnect so they run on real
    // connect/reconnect only. Per-channel message handlers are registered separately
    // below so navigation re-registers closures without cycling the transport.
  }, [token, onTokenChange, connect, disconnect, socket, setUnread, setOutbox, setDmPageOpen, setMembers, loadKanbanIfNeededRef, loadFormIfNeededRef, currentVoidIdRef, currentChannelIdRef, channelsRef, pendingReadRef, heartbeatRef, openChannelFromFqidRef]);
}

